/**
 * PricingCalculatorFormV2
 *
 * Redesigned pricing calculator form with improved UX:
 * - Two-column layout with sticky summary panel
 * - Collapsible accordion sections
 * - Preset configuration templates
 * - Toggle switches for binary options
 *
 * Business logic and state management are handled by usePricingCalculatorForm hook.
 * This component is responsible only for rendering the UI.
 *
 * @see src/modules/RetoolModule/hooks/usePricingCalculatorForm.tsx - Business logic
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Type definitions
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Tooltip,
  Alert,
  Collapse,
  Button,
  alpha,
  IconButton,
} from '@mui/material';
import { HelpCircle as HelpIcon, Pencil as PencilIcon, X as XIcon } from 'lucide-react';
import {
  Building2 as BuildingIcon,
  CreditCard as CreditCardIcon,
  Users as UsersIcon,
  Server as ServerIcon,
  Blocks as BlocksIcon,
  Link2 as LinkIcon,
} from 'lucide-react';

import {
  PricingCalculatorFormValues,
  PricingCalculatorSummary,
  PlanType,
  TenantType,
  isDedicatedTenant,
  PRICING_CONSTANTS,
  PricingPresetKey,
  calculateSsoFeePerUser,
  calculateIsolatedDatabaseFee,
  calculateTenantFee,
  calculateDirectDatabaseFee,
  calculatePlatformFee,
  calculateOnboardingGroups,
  getOnboardingFeePerGroup,
  computeSectionSubtotals,
  getPlatformFeeTierMinUsers,
  BASE_PLAN_FLAT_FEE,
  BASE_PLAN_FLAT_FEE_MAX_USERS,
  MOSAIC_ENTERPRISE_MIN_USERS,
  PLATFORM_FEE_TIER_TABLE,
  roundHoursUp,
} from '../../types/pricingCalculator';
import {
  usePricingCalculatorForm,
  getInitialFormValues,
} from '../../hooks/usePricingCalculatorForm';
import { usePricingCalculatorIntegrationsMultiSelector } from '../../hooks/usePricingCalculatorIntegrationsMultiSelector';
import { usePricingExport } from '../../hooks/usePricingExport';
import { usePricingCalculatorUrlParams } from '../../hooks/usePricingCalculatorUrlParams';
import { PricingCalculatorIntegrationsMultiSelector } from '../PricingCalculatorIntegrationsMultiSelector';
import { PricingCalculatorIntegrationDetail } from '../PricingCalculatorIntegrationDetail';
import { PricingScaleTable } from '../PricingScaleTable';

import {
  PricingCalculatorLayout,
  PricingCalculatorSection,
  PricingCalculatorStickyPanel,
  PricingCalculatorPresets,
  FeatureToggle,
  PricingCalculatorFormulas,
  TierIndicator,
  RenewalComparison,
  CurrentContractData,
} from './index';

/**
 * Data-isolation tier (GLX-1751). One dropdown replaces the former standalone
 * Isolated Database / Isolated AI / Single Tenant / Enterprise Tenant toggles.
 * It maps onto the existing form state (tenantType + isolatedDatabase +
 * isolatedAi) so the pricing engine is unchanged. Direct Database and Gov Cloud
 * stay as their own toggles.
 */
type DeploymentTier = 'multi' | 'isolated' | 'isolated-ai' | 'single' | 'enterprise';

const DEPLOYMENT_TIER_OPTIONS: { value: DeploymentTier; label: string }[] = [
  { value: 'multi', label: 'Multi-Tenant (shared)' },
  { value: 'isolated', label: 'Isolated Database' },
  { value: 'isolated-ai', label: 'Isolated AI' },
  { value: 'single', label: 'Single Tenant' },
  { value: 'enterprise', label: 'Enterprise Tenant' },
];

/** Derive the selected tier from form state. Enterprise/Single win over the
 * isolation booleans; Isolated AI wins over a plain Isolated Database. */
const getDeploymentTier = (v: PricingCalculatorFormValues): DeploymentTier =>
  v.tenantType === TenantType.ENTERPRISE
    ? 'enterprise'
    : v.tenantType === TenantType.SINGLE
      ? 'single'
      : v.isolatedAi
        ? 'isolated-ai'
        : v.isolatedDatabase
          ? 'isolated'
          : 'multi';

/**
 * Shared copy for the "Include Migration" toggle (GLX-1747). Single source of
 * truth for the fee + duration + downtime so the Direct Database and Isolated
 * Database controls can't drift apart.
 */
const MIGRATION_OPTION_DESCRIPTION = `One-time fee: $${PRICING_CONSTANTS.ISOLATED_DATABASE_MIGRATION_FEE.toLocaleString(
  undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 },
)} · 8 hours · one-day downtime`;

// Format currency for display
const formatCurrency = (amount: number) =>
  `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Standard-plan platform fee by tier, for the Platform Fee tooltip. Sampled from
 * the published scale (GLX-1931) rather than typed out, so the hint cannot drift
 * from what the field actually charges. Only the first few tiers get a line —
 * the full 26-tier scale is on the Tier card — so the trailing row is a floor.
 */
const PLATFORM_FEE_TOOLTIP_TIER_COUNT = 6;

// Sampled at each tier's UPPER bound. Base overrides the scale with a
// flat $999 at or below 15 members, so sampling tier 1 at its lower bound would
// misreport that tier the day this tooltip is made plan-aware.
const formatStandardAnnualFee = (members: number) =>
  `$${calculatePlatformFee(members, PlanType.STANDARD).platformFee.toLocaleString('en-US')}/yr`;

const PLATFORM_FEE_TOOLTIP_OVERFLOW_MIN_USERS = getPlatformFeeTierMinUsers(
  PLATFORM_FEE_TOOLTIP_TIER_COUNT,
);

const PLATFORM_FEE_TOOLTIP_ROWS: readonly string[] = [
  ...PLATFORM_FEE_TIER_TABLE.slice(0, PLATFORM_FEE_TOOLTIP_TIER_COUNT).map(
    (tier, index) =>
      `• ${getPlatformFeeTierMinUsers(index)}-${tier.maxUsers} members: ${formatStandardAnnualFee(
        tier.maxUsers,
      )}`,
  ),
  `• ${PLATFORM_FEE_TOOLTIP_OVERFLOW_MIN_USERS}+ members: from ${formatStandardAnnualFee(
    PLATFORM_FEE_TOOLTIP_OVERFLOW_MIN_USERS,
  )}`,
];

type AddonsSectionProps = {
  values: PricingCalculatorFormValues;
  setValues: React.Dispatch<React.SetStateAction<PricingCalculatorFormValues>>;
  summary: PricingCalculatorSummary;
  addonsSubtotal: number;
  expanded: boolean;
  onExpandedChange: () => void;
  handleInputChange: (
    field: keyof PricingCalculatorFormValues,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
};

type AddonRowsProps = Pick<
  AddonsSectionProps,
  'values' | 'setValues' | 'summary' | 'handleInputChange'
>;

/** Security Package SSO toggle, rate-tier tooltip, and discount panel. */
const AddonsSsoRows: React.FC<AddonRowsProps> = ({ values, setValues, handleInputChange }) => (
  <>
    {/* Security Package SSO */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Security Package SSO"
          checked={values.securityPackageSso}
          onChange={(checked) => setValues((prev) => ({ ...prev, securityPackageSso: checked }))}
          description="SAML-based Single Sign-On (Azure, Google)"
          price={
            values.securityPackageSso
              ? `${formatCurrency(calculateSsoFeePerUser(values.numberOfMembers))}/user/month`
              : undefined
          }
          docLink="https://readme.mosaicapp.com/docs/microsoft-azure-saml"
          name="securityPackageSso"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              SSO Price Per User (Monthly)
            </Typography>
            <Typography variant="body2">• 1-49 users: $0.50</Typography>
            <Typography variant="body2">• 50-99 users: $0.40</Typography>
            <Typography variant="body2">• 100-249 users: $0.38</Typography>
            <Typography variant="body2">• 250-499 users: $0.30</Typography>
            <Typography variant="body2">• 500-999 users: $0.25</Typography>
            <Typography variant="body2">• 1000-1999 users: $0.23</Typography>
            <Typography variant="body2">• 2000+ users: $0.21-$0.05</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Current: {values.numberOfMembers} users = $
              {calculateSsoFeePerUser(values.numberOfMembers).toFixed(2)}/user/mo
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.securityPackageSso && (
      <Box sx={{ ml: 4, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="SSO Discount"
          name="discountOnSecurityPackageSso"
          type="number"
          value={values.discountOnSecurityPackageSso || ''}
          onChange={handleInputChange('discountOnSecurityPackageSso')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
        {/* SSO Tier Indicator */}
        <Box sx={{ maxWidth: 400 }}>
          <TierIndicator
            userCount={values.numberOfMembers}
            tierType="sso"
            title="SSO Rate Tier"
            showComparison={true}
            showProgress={true}
            enableAnimations={true}
          />
        </Box>
      </Box>
    )}
  </>
);

/** AI Forecasting and Non-People Resources toggles with their discount inputs. */
const AddonsAiAndResourcesRows: React.FC<AddonRowsProps> = ({
  values,
  setValues,
  summary,
  handleInputChange,
}) => (
  <>
    {/* AI Forecasting */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="AI Forecasting"
          checked={values.aiForecasting}
          onChange={(checked) => setValues((prev) => ({ ...prev, aiForecasting: checked }))}
          description="35% of member + integration fees"
          price={
            values.aiForecasting
              ? formatCurrency(summary?.aiForecastingFeeAfterDiscount || 0)
              : undefined
          }
          name="aiForecasting"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              AI Forecasting Calculation
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              35% × (Member Fees + Integration Recurring)
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              {summary
                ? `Current: 35% × (${formatCurrency(summary.totalMemberCostYearly)} + ${formatCurrency(summary.integrationRecurringFeeAfterDiscount)}) = ${formatCurrency((summary.totalMemberCostYearly + summary.integrationRecurringFeeAfterDiscount) * 0.35)}`
                : 'Enable to see calculation'}
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.aiForecasting && (
      <Box sx={{ ml: 4, mb: 2 }}>
        <TextField
          label="AI Forecasting Discount"
          name="discountOnAiForecasting"
          type="number"
          value={values.discountOnAiForecasting || ''}
          onChange={handleInputChange('discountOnAiForecasting')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
      </Box>
    )}

    {/* Non-People Resources */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Non-People Resources"
          checked={values.nonPeopleResources}
          onChange={(checked) => setValues((prev) => ({ ...prev, nonPeopleResources: checked }))}
          description="50% of discounted license cost"
          price={
            values.nonPeopleResources
              ? formatCurrency(summary?.nonPeopleResourcesFeeAfterDiscount || 0)
              : undefined
          }
          name="nonPeopleResources"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Non-People Resources Calculation
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              50% × (Member License Cost after discount)
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              {summary
                ? `Current: 50% × ${formatCurrency(summary.totalMemberCostYearly)} = ${formatCurrency(summary.totalMemberCostYearly * 0.5)}`
                : 'Enable to see calculation'}
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.nonPeopleResources && (
      <Box sx={{ ml: 4, mb: 2 }}>
        <TextField
          label="NPR Discount"
          name="discountOnNonPeopleResources"
          type="number"
          value={values.discountOnNonPeopleResources || ''}
          onChange={handleInputChange('discountOnNonPeopleResources')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
      </Box>
    )}
  </>
);

/** Profile Building and Skills Tagging toggles with their discount inputs. */
const AddonsProfileAndSkillsRows: React.FC<AddonRowsProps> = ({
  values,
  setValues,
  summary,
  handleInputChange,
}) => (
  <>
    {/* Profile Building */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Profile Building"
          checked={values.profileBuilding}
          onChange={(checked) => setValues((prev) => ({ ...prev, profileBuilding: checked }))}
          description="$10 per user per month"
          price={
            values.profileBuilding
              ? formatCurrency(summary?.profileBuildingFeeAfterDiscount || 0)
              : undefined
          }
          name="profileBuilding"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Profile Building Pricing
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • $10 per user per month
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Automated profile enrichment
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Current: {values.numberOfMembers} users × $10 × {values.subscriptionDuration} mo ={' '}
              {formatCurrency(values.numberOfMembers * 10 * values.subscriptionDuration)}
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.profileBuilding && (
      <Box sx={{ ml: 4, mb: 2 }}>
        <TextField
          label="Profile Building Discount"
          name="discountOnProfileBuilding"
          type="number"
          value={values.discountOnProfileBuilding || ''}
          onChange={handleInputChange('discountOnProfileBuilding')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
      </Box>
    )}

    {/* Skills Tagging */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Skills Tagging"
          checked={values.skillsTagging}
          onChange={(checked) => setValues((prev) => ({ ...prev, skillsTagging: checked }))}
          description="$5 per user per month"
          price={
            values.skillsTagging
              ? formatCurrency(summary?.skillsTaggingFeeAfterDiscount || 0)
              : undefined
          }
          name="skillsTagging"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Skills Tagging Pricing
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • $5 per user per month
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • AI-powered skills identification
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Current: {values.numberOfMembers} users × $5 × {values.subscriptionDuration} mo ={' '}
              {formatCurrency(values.numberOfMembers * 5 * values.subscriptionDuration)}
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.skillsTagging && (
      <Box sx={{ ml: 4, mb: 2 }}>
        <TextField
          label="Skills Tagging Discount"
          name="discountOnSkillsTagging"
          type="number"
          value={values.discountOnSkillsTagging || ''}
          onChange={handleInputChange('discountOnSkillsTagging')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
      </Box>
    )}
  </>
);

/** Solutions Support hours and the White-Label toggle. */
const AddonsSupportAndWhiteLabelRows: React.FC<AddonRowsProps> = ({
  values,
  setValues,
  summary,
  handleInputChange,
}) => (
  <>
    {/* Solutions Support */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Solutions Support"
          checked={values.solutionsSupport}
          onChange={(checked) => setValues((prev) => ({ ...prev, solutionsSupport: checked }))}
          description="$200/hour in 5-hour increments"
          price={
            values.solutionsSupport ? formatCurrency(summary?.solutionsSupportFee || 0) : undefined
          }
          name="solutionsSupport"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Solutions Support Pricing
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • $200 per hour
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Sold in 5-hour increments
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Dedicated implementation support
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Current: {values.solutionsSupportHours} hours × $200 ={' '}
              {formatCurrency(values.solutionsSupportHours * 200)}
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.solutionsSupport && (
      <Box sx={{ ml: 4, mb: 2 }}>
        <TextField
          label="Support Hours"
          name="solutionsSupportHours"
          type="number"
          value={values.solutionsSupportHours}
          onChange={handleInputChange('solutionsSupportHours')}
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">hours</InputAdornment>,
            },
            htmlInput: { step: 5, min: 5 },
          }}
          sx={{ width: 150 }}
        />
      </Box>
    )}

    {/* White Label */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="White-Label"
          checked={values.enableWhiteLabel}
          onChange={(checked) => setValues((prev) => ({ ...prev, enableWhiteLabel: checked }))}
          description="20% increase in per-seat pricing"
          price={
            values.enableWhiteLabel
              ? formatCurrency(summary?.whiteLabelAdditionalCost || 0)
              : undefined
          }
          name="enableWhiteLabel"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              White-Label Pricing
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • 20% surcharge on per-seat pricing
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Custom branding and domain
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Current: 20% × {formatCurrency(summary?.totalMemberCostYearly || 0)} ={' '}
              {formatCurrency((summary?.totalMemberCostYearly || 0) * 0.2)}
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>
  </>
);

/**
 * Add-ons accordion. Extracted from the form's render closure to keep both
 * functions under the repo complexity ceilings; rendering is unchanged.
 */
const AddonsSection: React.FC<AddonsSectionProps> = ({
  values,
  setValues,
  summary,
  addonsSubtotal,
  expanded,
  onExpandedChange,
  handleInputChange,
}) => {
  const rowProps: AddonRowsProps = { values, setValues, summary, handleInputChange };

  return (
    <PricingCalculatorSection
      title="Add-ons"
      subtitle={formatCurrency(addonsSubtotal)}
      icon={<BlocksIcon size={18} />}
      hasSelections={addonsSubtotal > 0}
      defaultExpanded={false}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <AddonsSsoRows {...rowProps} />
      <AddonsAiAndResourcesRows {...rowProps} />
      <AddonsProfileAndSkillsRows {...rowProps} />
      <AddonsSupportAndWhiteLabelRows {...rowProps} />
    </PricingCalculatorSection>
  );
};

type ImplementationOptionsSectionProps = {
  values: PricingCalculatorFormValues;
  setValues: React.Dispatch<React.SetStateAction<PricingCalculatorFormValues>>;
  summary: PricingCalculatorSummary;
  handleInputChange: ReturnType<typeof usePricingCalculatorForm>['handleInputChange'];
  serverSubtotal: number;
  expanded: boolean;
  onExpandedChange: () => void;
  handleDirectDatabaseChange: ReturnType<
    typeof usePricingCalculatorForm
  >['handleDirectDatabaseChange'];
};

/**
 * Direct Database, Data Isolation Tier, and Gov Cloud accordion. Extracted from
 * the form's render closure to keep both functions under the repo complexity
 * ceilings; rendering is unchanged.
 */
const ImplementationOptionsSection: React.FC<ImplementationOptionsSectionProps> = ({
  values,
  setValues,
  summary,
  handleInputChange,
  serverSubtotal,
  expanded,
  onExpandedChange,
  handleDirectDatabaseChange,
}) => (
  <PricingCalculatorSection
    title="Implementation Options"
    subtitle={formatCurrency(serverSubtotal)}
    icon={<ServerIcon size={18} />}
    hasSelections={serverSubtotal > 0}
    defaultExpanded={false}
    expanded={expanded}
    onExpandedChange={onExpandedChange}
  >
    {/* Direct Database */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Direct Database Integration"
          checked={values.directDatabaseIntegration || isDedicatedTenant(values.tenantType)}
          onChange={(checked) => {
            handleDirectDatabaseChange({
              target: { checked },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
          disabled={isDedicatedTenant(values.tenantType)}
          description="Enables direct database access. Automatically enables Isolated Database."
          price={
            values.directDatabaseIntegration && !isDedicatedTenant(values.tenantType)
              ? formatCurrency(
                  calculateDirectDatabaseFee(
                    values.numberOfMembers,
                    values.subscriptionDuration,
                    values.discountOnDirectDatabase,
                  ),
                )
              : undefined
          }
          note={isDedicatedTenant(values.tenantType) ? 'Included in dedicated tenant' : undefined}
          docLink="https://readme.mosaicapp.com/docs/direct-database-schema"
          name="directDatabaseIntegration"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Direct Database Pricing (12 mo)
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Base: $50/user ($2,000 min for ≤40 users)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Volume Discounts (per 10-user bracket):
            </Typography>
            <Typography variant="body2">• 41-50 users: 5% off</Typography>
            <Typography variant="body2">• 51-60 users: 10% off</Typography>
            <Typography variant="body2">• 61-70 users: 15% off</Typography>
            <Typography variant="body2">• 71-80 users: 20% off</Typography>
            <Typography variant="body2">• ...and so on</Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Prorated based on {values.subscriptionDuration} mo duration
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.directDatabaseIntegration && (
      <Box sx={{ ml: 4, mb: 2, display: 'flex', alignItems: 'flex-start', gap: 3 }}>
        <TextField
          label="Direct Database Discount"
          name="discountOnDirectDatabase"
          type="number"
          value={values.discountOnDirectDatabase || ''}
          onChange={handleInputChange('discountOnDirectDatabase')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
        <FeatureToggle
          label="Include Migration"
          checked={values.isolatedDatabaseMigration}
          onChange={(checked) =>
            setValues((prev) => ({ ...prev, isolatedDatabaseMigration: checked }))
          }
          description={MIGRATION_OPTION_DESCRIPTION}
          name="isolatedDatabaseMigration"
        />
      </Box>
    )}

    {/* Data Isolation Tier (GLX-1751) — single dropdown replacing the
            standalone Isolated Database / Isolated AI / Single Tenant /
            Enterprise Tenant toggles. Direct Database and Gov Cloud remain
            their own toggles. */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Data Isolation Tier
      </Typography>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Deployment / Data Isolation
            </Typography>
            <Typography variant="body2">
              • Multi-Tenant: shared infrastructure (no extra fee)
            </Typography>
            <Typography variant="body2">
              • Isolated Database: dedicated DB ($100/mo + $0.50/user over 100)
            </Typography>
            <Typography variant="body2">
              {`• Isolated AI: dedicated AI env ($${PRICING_CONSTANTS.ISOLATED_AI_FEE.toLocaleString()} one-time, includes Isolated DB)`}
            </Typography>
            <Typography variant="body2">
              • Single Tenant: fully dedicated ($48,000/yr base)
            </Typography>
            <Typography variant="body2">
              • Enterprise Tenant: dedicated at 60% of Single Tenant
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Teams of 100 or more require at least an Isolated Database.
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary' }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    <FormControl sx={{ width: 320, mb: 1.5 }} disabled={values.directDatabaseIntegration}>
      <InputLabel id="deployment-tier-label">Data Isolation Tier</InputLabel>
      <Select
        labelId="deployment-tier-label"
        label="Data Isolation Tier"
        // Always derive from state so the control reflects the true tier
        // (e.g. "Isolated AI") even when Direct Database forces it disabled.
        value={getDeploymentTier(values)}
        onChange={(e) => {
          const tier = e.target.value as DeploymentTier;
          setValues((prev) => {
            switch (tier) {
              case 'isolated':
                return {
                  ...prev,
                  tenantType: TenantType.SHARED,
                  isolatedDatabase: true,
                  isolatedAi: false,
                  directDatabaseIntegration: false,
                };
              case 'isolated-ai':
                return {
                  ...prev,
                  tenantType: TenantType.SHARED,
                  isolatedDatabase: true,
                  isolatedAi: true,
                  directDatabaseIntegration: false,
                };
              case 'single':
                return {
                  ...prev,
                  tenantType: TenantType.SINGLE,
                  isolatedDatabase: false,
                  isolatedAi: false,
                  directDatabaseIntegration: false,
                };
              case 'enterprise':
                return {
                  ...prev,
                  tenantType: TenantType.ENTERPRISE,
                  isolatedDatabase: false,
                  isolatedAi: false,
                  directDatabaseIntegration: false,
                };
              case 'multi':
              default:
                return {
                  ...prev,
                  tenantType: TenantType.SHARED,
                  isolatedDatabase: false,
                  isolatedAi: false,
                  directDatabaseIntegration: false,
                };
            }
          });
        }}
      >
        {DEPLOYMENT_TIER_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            disabled={opt.value === 'multi' && values.numberOfMembers >= 100}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    {/* Contextual price + per-tier discount/migration controls (GLX-1751) */}
    {values.directDatabaseIntegration ? (
      <Box sx={{ ml: 0.5, mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'info.main',
          }}
        >
          Isolated database is included with Direct Database (configured above). Disable Direct
          Database to choose a different isolation tier.
        </Typography>
        {values.isolatedAi && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            Isolated AI also applies: {formatCurrency(PRICING_CONSTANTS.ISOLATED_AI_FEE)} one-time.
          </Typography>
        )}
      </Box>
    ) : (
      (() => {
        const tier = getDeploymentTier(values);
        const migrationToggle = (
          <FeatureToggle
            label="Include Migration"
            checked={values.isolatedDatabaseMigration}
            onChange={(checked) =>
              setValues((prev) => ({ ...prev, isolatedDatabaseMigration: checked }))
            }
            description={MIGRATION_OPTION_DESCRIPTION}
            name="isolatedDatabaseMigration"
          />
        );
        switch (tier) {
          case 'multi':
            return (
              <Box sx={{ ml: 0.5, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Shared infrastructure — no additional fee.
                </Typography>
              </Box>
            );
          case 'isolated':
            return (
              <Box sx={{ ml: 0.5, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  Isolated Database:{' '}
                  {formatCurrency(calculateIsolatedDatabaseFee(values.numberOfMembers))}/month
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                  <TextField
                    label="Isolated Database Discount"
                    name="discountOnIsolatedDatabase"
                    type="number"
                    value={values.discountOnIsolatedDatabase || ''}
                    onChange={handleInputChange('discountOnIsolatedDatabase')}
                    placeholder="0"
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      },
                    }}
                    sx={{ width: 150 }}
                  />
                  {migrationToggle}
                </Box>
              </Box>
            );
          case 'isolated-ai':
            // No migration toggle here: the Isolated DB is bundled with
            // Isolated AI, so it isn't a standalone migration (GLX-1747).
            return (
              <Box sx={{ ml: 0.5, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  Isolated AI: {formatCurrency(PRICING_CONSTANTS.ISOLATED_AI_FEE)} one-time ·
                  includes Isolated Database
                </Typography>
              </Box>
            );
          case 'single':
          case 'enterprise':
            return (
              <Box sx={{ ml: 0.5, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  {tier === 'enterprise' ? 'Enterprise Tenant' : 'Single Tenant'}:{' '}
                  {formatCurrency(
                    calculateTenantFee(
                      values.tenantType,
                      values.numberOfMembers,
                      values.subscriptionDuration,
                    ),
                  )}{' '}
                  · prorated by {values.subscriptionDuration} mo
                </Typography>
                <TextField
                  label={
                    tier === 'enterprise' ? 'Enterprise Tenant Discount' : 'Single Tenant Discount'
                  }
                  name="discountOnTenant"
                  type="number"
                  value={values.discountOnTenant || ''}
                  onChange={handleInputChange('discountOnTenant')}
                  placeholder="0"
                  slotProps={{
                    input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                  }}
                  sx={{ width: 200 }}
                />
              </Box>
            );
          default:
            return null;
        }
      })()
    )}

    {/* Gov Cloud */}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FeatureToggle
          label="Gov Cloud"
          checked={values.govCloud}
          onChange={(checked) =>
            setValues((prev) => ({
              ...prev,
              govCloud: checked,
              // Gov Cloud requires a dedicated tenant — auto-promote to Single Tenant when enabled,
              // but don't downgrade an already-selected Enterprise Tenant.
              tenantType: checked
                ? isDedicatedTenant(prev.tenantType)
                  ? prev.tenantType
                  : TenantType.SINGLE
                : prev.tenantType,
              // - Disable Direct Database (mutually exclusive with dedicated tenant)
              directDatabaseIntegration: checked ? false : prev.directDatabaseIntegration,
              // - Disable standalone Isolated Database (included in dedicated tenant)
              isolatedDatabase: checked ? false : prev.isolatedDatabase,
            }))
          }
          description="FedRAMP compliant hosting with 20% surcharge"
          price={
            values.govCloud
              ? `20% surcharge: ${formatCurrency(summary?.govCloudFeeAfterDiscount || 0)}`
              : undefined
          }
          docLink="https://readme.mosaicapp.com/docs/govcloud"
          name="govCloud"
        />
      </Box>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Gov Cloud Pricing
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • 20% surcharge on total recurring fees
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • FedRAMP compliant infrastructure
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              Requires Single Tenant (auto-enabled)
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {values.govCloud && (
      <Box sx={{ ml: 4, mb: 2 }}>
        <TextField
          label="Gov Cloud Discount"
          name="discountOnGovCloudFee"
          type="number"
          value={values.discountOnGovCloudFee || ''}
          onChange={handleInputChange('discountOnGovCloudFee')}
          placeholder="0"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            },
          }}
          sx={{ width: 200 }}
        />
      </Box>
    )}
  </PricingCalculatorSection>
);

interface PricingCalculatorFormV2Props {
  initialValues?: Partial<PricingCalculatorFormValues>;
  onSubmit?: (values: PricingCalculatorFormValues) => void;
}

type PlatformOnboardingSectionPropsType = {
  values: PricingCalculatorFormValues;
  summary: PricingCalculatorSummary | null;
  handleInputChange: ReturnType<typeof usePricingCalculatorForm>['handleInputChange'];
  platformSubtotal: number;
  expanded: boolean;
  onExpandedChange: () => void;
};

/** Platform-fee and onboarding-fee inputs plus their discounted totals. */
const PlatformOnboardingSection = ({
  values,
  summary,
  handleInputChange,
  platformSubtotal,
  expanded,
  onExpandedChange,
}: PlatformOnboardingSectionPropsType) => (
  <PricingCalculatorSection
    title="Platform & Onboarding"
    subtitle={formatCurrency(platformSubtotal)}
    icon={<CreditCardIcon size={18} />}
    hasSelections={platformSubtotal > 0}
    defaultExpanded={true}
    expanded={expanded}
    onExpandedChange={onExpandedChange}
  >
    {/* Platform Fee */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Platform Fee
      </Typography>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Tier-Based Platform Fee (Annual, Standard plan)
            </Typography>
            {PLATFORM_FEE_TOOLTIP_ROWS.map((row) => (
              <Typography variant="body2" key={row}>
                {row}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Base (≤{BASE_PLAN_FLAT_FEE_MAX_USERS} users): Fixed $
              {BASE_PLAN_FLAT_FEE.toLocaleString('en-US')}/yr, 0 hrs.{' '}
              {BASE_PLAN_FLAT_FEE_MAX_USERS + 1}+ users: its own published rate. Mosaic Enterprise (
              {MOSAIC_ENTERPRISE_MIN_USERS}+ users): its own published rate, ≈2× Standard, with 2×
              hours. IT Customer: 50% off.
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary' }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      <TextField
        label="Platform Fee"
        name="platformFee"
        type="number"
        value={values.platformFee}
        onChange={handleInputChange('platformFee')}
        helperText={(() => {
          const platformFeeData = calculatePlatformFee(values.numberOfMembers, values.planType);
          // Mirror computePricingSummary's IT-Customer halving so this
          // helper text stays consistent with the sticky-panel total and
          // any exported quote (see computePricingSummary's hoursIncluded
          // assignment in pricingCalculator.ts).
          const hoursIncluded =
            summary?.hoursIncluded ??
            (values.isItCustomer
              ? roundHoursUp(Math.round(platformFeeData.hoursIncluded / 2))
              : platformFeeData.hoursIncluded);
          const hoursPart = hoursIncluded > 0 ? `Hours included: ${hoursIncluded}` : '';

          let notePart = '';
          if (
            values.planType === PlanType.BASE &&
            values.numberOfMembers <= BASE_PLAN_FLAT_FEE_MAX_USERS
          ) {
            notePart = `Fixed at $${BASE_PLAN_FLAT_FEE.toLocaleString('en-US')} for Base (≤${BASE_PLAN_FLAT_FEE_MAX_USERS} users)`;
          } else if (values.planType === PlanType.BASE) {
            notePart = values.isItCustomer
              ? 'Base rate (16+ users), then 50% IT discount'
              : 'Base rate (16+ users)';
          } else if (values.planType === PlanType.MOSAIC_ENTERPRISE) {
            notePart =
              values.numberOfMembers < MOSAIC_ENTERPRISE_MIN_USERS
                ? `Enterprise not available for <${MOSAIC_ENTERPRISE_MIN_USERS} users`
                : values.isItCustomer
                  ? 'Published Enterprise rate, then 50% IT discount'
                  : 'Published Enterprise rate (≈2× Standard)';
          } else if (values.isItCustomer) {
            notePart = '50% discount applied';
          }

          return [notePart, hoursPart].filter(Boolean).join(' · ');
        })()}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
        }}
        sx={{ width: 200 }}
      />
      <TextField
        label="Discount"
        name="discountOnPlatformFee"
        type="number"
        value={values.discountOnPlatformFee || ''}
        onChange={handleInputChange('discountOnPlatformFee')}
        placeholder="0"
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
        }}
        sx={{ width: 120 }}
      />
      <TextField
        label="After Discount"
        value={summary?.platformFeeYearly.toFixed(2) || '0.00'}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
          htmlInput: { readOnly: true },
        }}
        disabled
        sx={{ width: 150 }}
      />
    </Box>

    {/* Onboarding Fee */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Onboarding Fee
      </Typography>
      <Tooltip
        title={
          <Box sx={{ p: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Onboarding Groups Calculation
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {`• $${PRICING_CONSTANTS.ONBOARDING_FEE_PER_GROUP.toLocaleString()} per group ($${PRICING_CONSTANTS.ONBOARDING_FEE_PER_GROUP_ENTERPRISE.toLocaleString()} per group on the Mosaic Enterprise Plan)`}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • {PRICING_CONSTANTS.MEMBERS_PER_ONBOARDING_GROUP} members per group capacity
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Add a group once a partial group reaches{' '}
              {PRICING_CONSTANTS.ONBOARDING_GROUP_THRESHOLD} members
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Example: 400 members = 2 groups, 650 members = 3 groups
            </Typography>
          </Box>
        }
        arrow
        placement="right"
      >
        <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary' }}>
          <HelpIcon size={16} />
        </Box>
      </Tooltip>
    </Box>

    {/* Auto-calculated groups info */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        Based on {values.numberOfMembers} members:
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
        {calculateOnboardingGroups(values.numberOfMembers)} group
        {calculateOnboardingGroups(values.numberOfMembers) !== 1 ? 's' : ''}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        × ${getOnboardingFeePerGroup(values.planType).toLocaleString()} = $
        {(
          calculateOnboardingGroups(values.numberOfMembers) *
          getOnboardingFeePerGroup(values.planType)
        ).toLocaleString()}
      </Typography>
    </Box>

    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <TextField
        label="Onboarding Fee"
        name="onboardingFee"
        type="number"
        value={values.onboardingFee || ''}
        onChange={handleInputChange('onboardingFee')}
        placeholder="0"
        helperText={`Auto-calculated based on member count · Hours included: ${
          PRICING_CONSTANTS.ONBOARDING_HOURS_PER_GROUP *
          calculateOnboardingGroups(values.numberOfMembers)
        } (${PRICING_CONSTANTS.ONBOARDING_HOURS_PER_GROUP}/group)`}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
        }}
        sx={{ width: 200 }}
      />
      <TextField
        label="Discount"
        name="discountOnOnboardingFee"
        type="number"
        value={values.discountOnOnboardingFee || ''}
        onChange={handleInputChange('discountOnOnboardingFee')}
        placeholder="0"
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
        }}
        sx={{ width: 120 }}
      />
      <TextField
        label="After Discount"
        value={summary?.onboardingFeeAfterDiscount.toFixed(2) || '0.00'}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
          htmlInput: { readOnly: true },
        }}
        disabled
        sx={{ width: 150 }}
      />
    </Box>
  </PricingCalculatorSection>
);

export const PricingCalculatorFormV2: React.FC<PricingCalculatorFormV2Props> = ({
  initialValues: providedInitialValues,
  onSubmit,
}) => {
  const theme = useTheme();

  const [activePreset, setActivePreset] = useState<PricingPresetKey | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [allSectionsExpanded, setAllSectionsExpanded] = useState(true);

  // Renewal comparison state
  const [currentContract, setCurrentContract] = useState<CurrentContractData | null>(null);
  const [isEditingBaseline, setIsEditingBaseline] = useState(false); // true = configuring baseline, false = configuring proposal
  const [savedProposalValues, setSavedProposalValues] =
    useState<PricingCalculatorFormValues | null>(null); // Store proposal before editing baseline

  // Individual section states for Calculator tab
  const [sectionStates, setSectionStates] = useState({
    baseInfo: true,
    memberPricing: true,
    platformOnboarding: true,
    serverUpgrades: false,
    addons: false,
    integrations: false,
  });

  // Toggle individual section
  const toggleSection = useCallback((section: keyof typeof sectionStates) => {
    setSectionStates((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const handleToggleExpandAll = useCallback(() => {
    setAllSectionsExpanded((prev) => {
      const newValue = !prev;
      // Also update individual section states when global toggle is clicked
      setSectionStates({
        baseInfo: newValue,
        memberPricing: newValue,
        platformOnboarding: newValue,
        serverUpgrades: newValue,
        addons: newValue,
        integrations: newValue,
      });
      return newValue;
    });
  }, []);

  // Use the hook for all state management and business logic
  const {
    values,
    setValues,
    summary,
    integrationMigrations,
    integrationSetupDiscounts,
    integrationRecurringDiscounts,
    integrationPipelineOpportunities,
    originalIntegrations,
    afterDiscountInput,
    setUserAfterDiscountInput,
    isEditingAfterDiscountRef,
    handleInputChange,
    handleNumberOfMembersChange,
    handlePlanTypeChange,
    handleDirectDatabaseChange,
    handleIsItCustomerChange,
    handleIntegrationsChange,
    handleFormSubmit,
    handleIntegrationMigrationChange,
    handleIntegrationDiscountChange,
    handleIntegrationPipelineOpportunitiesChange,
  } = usePricingCalculatorForm({
    initialValues: providedInitialValues,
    onSubmit,
  });

  // Use the integrations multi-selector hook with onChange callback
  const integrationsSelectorProps = usePricingCalculatorIntegrationsMultiSelector({
    integrationsToExclude: [],
    initialSelectedIntegrations: values.selectedIntegrations,
    onChange: handleIntegrationsChange,
  });

  // Use the pricing export hook for Copy/Excel/PDF exports
  const { exportToPDF, exportToExcel, copyQuoteToClipboard } = usePricingExport({
    values,
    summary,
  });

  // Generate default values for URL params comparison
  const defaultFormValues = useMemo(() => getInitialFormValues(), []);

  // Use URL params hook for shareable links
  const { getShareableUrl, urlInitialTab } = usePricingCalculatorUrlParams({
    values,
    defaultValues: defaultFormValues,
    syncToUrl: true,
    activeTab,
  });

  // Initialize activeTab from URL on mount
  useEffect(() => {
    if (urlInitialTab !== 0) {
      setActiveTab(urlInitialTab);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture current form values as baseline
  const captureBaseline = useCallback(() => {
    // When isRenewal is true, summary.totalCost excludes onboarding (since renewals don't pay onboarding).
    // But for the baseline, we want to capture the FULL previous contract value including onboarding.
    // So we add back the onboarding fee (after discount) to get the true previous contract value.
    const onboardingFeeAfterDiscount =
      values.onboardingFee * (1 - values.discountOnOnboardingFee / 100);
    const baselineTotalPrice = values.isRenewal
      ? (summary?.totalCost || 0) + onboardingFeeAfterDiscount
      : summary?.totalCost || 0;

    const contract: CurrentContractData = {
      numberOfMembers: values.numberOfMembers,
      numberOfInternalContractors: values.numberOfInternalContractors,
      numberOfExternalContractors: values.numberOfExternalContractors,
      numberOfGuests: values.numberOfGuests,
      subscriptionDuration: values.subscriptionDuration,
      totalPrice: baselineTotalPrice,
      // Pricing configuration
      planType: values.planType,
      pricePerMember: values.pricePerMember,
      discountOnMemberPrice: values.discountOnMemberPrice,
      platformFee: values.platformFee,
      discountOnPlatformFee: values.discountOnPlatformFee,
      onboardingFee: values.onboardingFee,
      discountOnOnboardingFee: values.discountOnOnboardingFee,
      additionalEnterpriseSupportHours: values.additionalEnterpriseSupportHours,
      // Features with discounts
      securityPackageSso: values.securityPackageSso,
      discountOnSecurityPackageSso: values.discountOnSecurityPackageSso,
      isolatedDatabase: values.isolatedDatabase,
      discountOnIsolatedDatabase: values.discountOnIsolatedDatabase,
      isolatedDatabaseMigration: values.isolatedDatabaseMigration,
      directDatabaseIntegration: values.directDatabaseIntegration,
      discountOnDirectDatabase: values.discountOnDirectDatabase,
      tenantType: values.tenantType,
      discountOnTenant: values.discountOnTenant,
      govCloud: values.govCloud,
      discountOnGovCloudFee: values.discountOnGovCloudFee,
      aiForecasting: values.aiForecasting,
      discountOnAiForecasting: values.discountOnAiForecasting,
      isolatedAi: values.isolatedAi,
      nonPeopleResources: values.nonPeopleResources,
      discountOnNonPeopleResources: values.discountOnNonPeopleResources,
      profileBuilding: values.profileBuilding,
      discountOnProfileBuilding: values.discountOnProfileBuilding,
      skillsTagging: values.skillsTagging,
      discountOnSkillsTagging: values.discountOnSkillsTagging,
      solutionsSupport: values.solutionsSupport,
      solutionsSupportHours: values.solutionsSupportHours,
      enableWhiteLabel: values.enableWhiteLabel,
      // Integrations
      selectedIntegrations: [...values.selectedIntegrations],
      discountOnIntegrationSetup: values.discountOnIntegrationSetup,
      discountOnIntegrationRecurring: values.discountOnIntegrationRecurring,
      integrationMigration: values.integrationMigration,
      enable4xDataTransfer: values.enable4xDataTransfer,
      bakIntegrationFetchTimes: values.bakIntegrationFetchTimes,
    };
    setCurrentContract(contract);
    setIsEditingBaseline(false);
    // If we were editing baseline, restore the saved proposal values
    if (savedProposalValues) {
      setValues(savedProposalValues);
      setSavedProposalValues(null);
    } else {
      // Set onboarding fee to 0 for the renewal proposal (baseline captured the previous onboarding cost)
      setValues((prev) => ({
        ...prev,
        onboardingFee: 0,
        discountOnOnboardingFee: 0,
      }));
    }
  }, [values, summary, savedProposalValues, setValues]);

  // Restore baseline values to form for editing
  const editBaseline = useCallback(() => {
    if (currentContract) {
      // Save current proposal values before overwriting with baseline
      setSavedProposalValues({ ...values });

      setValues((prev) => ({
        ...prev,
        numberOfMembers: currentContract.numberOfMembers,
        numberOfInternalContractors: currentContract.numberOfInternalContractors,
        numberOfExternalContractors: currentContract.numberOfExternalContractors,
        numberOfGuests: currentContract.numberOfGuests,
        subscriptionDuration: currentContract.subscriptionDuration,
        // Pricing configuration
        planType: currentContract.planType as PlanType,
        pricePerMember: currentContract.pricePerMember,
        discountOnMemberPrice: currentContract.discountOnMemberPrice,
        platformFee: currentContract.platformFee,
        discountOnPlatformFee: currentContract.discountOnPlatformFee,
        onboardingFee: currentContract.onboardingFee,
        discountOnOnboardingFee: currentContract.discountOnOnboardingFee,
        additionalEnterpriseSupportHours: currentContract.additionalEnterpriseSupportHours,
        // Features with discounts
        securityPackageSso: currentContract.securityPackageSso,
        discountOnSecurityPackageSso: currentContract.discountOnSecurityPackageSso,
        isolatedDatabase: currentContract.isolatedDatabase,
        discountOnIsolatedDatabase: currentContract.discountOnIsolatedDatabase,
        isolatedDatabaseMigration: currentContract.isolatedDatabaseMigration,
        directDatabaseIntegration: currentContract.directDatabaseIntegration,
        discountOnDirectDatabase: currentContract.discountOnDirectDatabase,
        tenantType: currentContract.tenantType,
        discountOnTenant: currentContract.discountOnTenant,
        govCloud: currentContract.govCloud,
        discountOnGovCloudFee: currentContract.discountOnGovCloudFee,
        aiForecasting: currentContract.aiForecasting,
        discountOnAiForecasting: currentContract.discountOnAiForecasting,
        isolatedAi: currentContract.isolatedAi,
        nonPeopleResources: currentContract.nonPeopleResources,
        discountOnNonPeopleResources: currentContract.discountOnNonPeopleResources,
        profileBuilding: currentContract.profileBuilding,
        discountOnProfileBuilding: currentContract.discountOnProfileBuilding,
        skillsTagging: currentContract.skillsTagging,
        discountOnSkillsTagging: currentContract.discountOnSkillsTagging,
        solutionsSupport: currentContract.solutionsSupport,
        solutionsSupportHours: currentContract.solutionsSupportHours,
        enableWhiteLabel: currentContract.enableWhiteLabel,
        // Integrations
        selectedIntegrations: [...currentContract.selectedIntegrations],
        discountOnIntegrationSetup: currentContract.discountOnIntegrationSetup,
        discountOnIntegrationRecurring: currentContract.discountOnIntegrationRecurring,
        integrationMigration: currentContract.integrationMigration,
        enable4xDataTransfer: currentContract.enable4xDataTransfer,
        bakIntegrationFetchTimes: currentContract.bakIntegrationFetchTimes,
      }));
      setIsEditingBaseline(true);
    }
  }, [currentContract, setValues, values]);

  // Cancel baseline editing and restore proposal values
  const cancelBaselineEdit = useCallback(() => {
    if (savedProposalValues) {
      setValues(savedProposalValues);
      setSavedProposalValues(null);
    }
    setIsEditingBaseline(false);
  }, [savedProposalValues, setValues]);

  // Handle preset selection
  const handlePresetSelect = useCallback(
    (presetKey: PricingPresetKey, presetValues: Partial<PricingCalculatorFormValues>) => {
      setActivePreset(presetKey);
      setValues((prev) => ({
        ...prev,
        ...presetValues,
        // Keep company name if already set
        companyName: prev.companyName || presetValues.companyName || '',
      }));
    },
    [setValues],
  );

  // Handle reset to defaults
  const handleReset = useCallback(() => {
    setActivePreset(null);
    // Use getInitialFormValues to properly calculate tier-based pricing
    const defaultValues = getInitialFormValues({ numberOfMembers: 1 });
    setValues(defaultValues);
  }, [setValues]);

  // Form validation for inline feedback
  const validation = useMemo(() => {
    const warnings: string[] = [];
    const errors: Record<string, string> = {};

    // Company name is recommended for export
    if (!values.companyName.trim()) {
      errors.companyName = 'Required for quote export';
    }

    // Minimum member count warning
    if (values.numberOfMembers < 1) {
      errors.numberOfMembers = 'At least 1 member required';
    }

    // Conflicting options: Direct DB and a dedicated tenant
    if (values.directDatabaseIntegration && isDedicatedTenant(values.tenantType)) {
      warnings.push('Direct Database and dedicated tenant cannot be enabled together');
    }

    // Isolated AI requires Isolated Database
    if (
      values.isolatedAi &&
      !values.isolatedDatabase &&
      !values.directDatabaseIntegration &&
      !isDedicatedTenant(values.tenantType)
    ) {
      warnings.push('Isolated AI requires Isolated Database to be enabled');
    }

    // Gov Cloud recommendation
    if (values.govCloud && !isDedicatedTenant(values.tenantType) && !values.securityPackageSso) {
      warnings.push('Gov Cloud customers typically require a dedicated tenant or SSO');
    }

    return { warnings, errors, hasErrors: Object.keys(errors).length > 0 };
  }, [values]);

  // Section subtotals are derived from the same shared helper the Sticky panel
  // uses, so the two views can never disagree on the same line item.
  const sectionSubtotals = useMemo(
    () =>
      summary
        ? computeSectionSubtotals(summary)
        : {
            memberPricingSubtotal: 0,
            platformSubtotal: 0,
            serverSubtotal: 0,
            govCloudSubtotal: 0,
            addonsSubtotal: 0,
            integrationsSubtotal: 0,
          },
    [summary],
  );
  const {
    memberPricingSubtotal,
    platformSubtotal,
    serverSubtotal,
    addonsSubtotal,
    integrationsSubtotal,
  } = sectionSubtotals;

  // Render the form content (left column)
  const renderFormContent = () => (
    <form onSubmit={handleFormSubmit}>
      {/* Renewal Comparison Panel - shows when renewal is enabled and current contract is set */}
      {values.isRenewal && currentContract && (
        <RenewalComparison
          currentContract={currentContract}
          proposedMembers={values.numberOfMembers}
          proposedInternalContractors={values.numberOfInternalContractors}
          proposedExternalContractors={values.numberOfExternalContractors}
          proposedGuests={values.numberOfGuests}
          proposedDuration={values.subscriptionDuration}
          proposedSummary={summary}
          proposedSecurityPackageSso={values.securityPackageSso}
          proposedIsolatedDatabase={values.isolatedDatabase}
          proposedDirectDatabaseIntegration={values.directDatabaseIntegration}
          proposedTenantType={values.tenantType}
          proposedGovCloud={values.govCloud}
          proposedAiForecasting={values.aiForecasting}
          proposedIsolatedAi={values.isolatedAi}
          proposedNonPeopleResources={values.nonPeopleResources}
          proposedProfileBuilding={values.profileBuilding}
          proposedSkillsTagging={values.skillsTagging}
          proposedEnableWhiteLabel={values.enableWhiteLabel}
          proposedSelectedIntegrations={values.selectedIntegrations}
          isVisible={true}
        />
      )}

      {/* Validation Warnings */}
      <Collapse in={validation.warnings.length > 0}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Configuration Warnings
          </Typography>
          {validation.warnings.map((warning, index) => (
            <Typography key={index} variant="body2" sx={{ fontSize: '0.85rem' }}>
              • {warning}
            </Typography>
          ))}
        </Alert>
      </Collapse>

      {/* Base Information Section */}
      <PricingCalculatorSection
        title="Base Information"
        subtitle={``}
        icon={<BuildingIcon size={18} />}
        hasSelections={values.companyName.length > 0 || values.numberOfMembers > 1}
        defaultExpanded={true}
        expanded={sectionStates.baseInfo}
        onExpandedChange={() => toggleSection('baseInfo')}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              label="Company Name"
              name="companyName"
              value={values.companyName}
              onChange={handleInputChange('companyName')}
              error={!!validation.errors.companyName}
              helperText={validation.errors.companyName}
              sx={{ flex: '1 1 300px' }}
            />
            <TextField
              label="Subscription Duration"
              name="subscriptionDuration"
              type="number"
              value={values.subscriptionDuration}
              onChange={handleInputChange('subscriptionDuration')}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">Months</InputAdornment>,
                },
                htmlInput: { min: 3 },
              }}
              sx={{ width: 200 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FeatureToggle
              label="Renewal"
              checked={values.isRenewal}
              onChange={(checked) => {
                setValues((prev) => ({ ...prev, isRenewal: checked }));
                // Clear baseline, editing state, and saved proposal values when toggling OFF
                if (!checked) {
                  setCurrentContract(null);
                  setIsEditingBaseline(false);
                  setSavedProposalValues(null);
                }
              }}
              description="Check if this is a renewal subscription"
              name="isRenewal"
            />
            <FeatureToggle
              label="IT Customer"
              checked={values.isItCustomer}
              onChange={(checked) => {
                handleIsItCustomerChange({
                  target: { checked },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              description="50% discount on platform fee"
              name="isItCustomer"
            />
          </Box>

          {/* Renewal Comparison Setup */}
          <Collapse in={values.isRenewal}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: '12px',
                backgroundColor:
                  !currentContract || isEditingBaseline
                    ? alpha(theme.palette.warning.main, 0.08)
                    : alpha(theme.palette.info.main, 0.05),
                border: '2px solid',
                borderColor:
                  !currentContract || isEditingBaseline
                    ? alpha(theme.palette.warning.main, 0.4)
                    : alpha(theme.palette.info.main, 0.2),
                transition: 'all 0.2s ease',
              }}
            >
              {!currentContract ? (
                // Step 1: No baseline set yet - prompt to configure and capture
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: theme.palette.warning.main,
                    }}
                  >
                    Step 1: Configure Current Contract
                    <Tooltip
                      title="First, enter what the customer currently has, then click 'Set as Baseline'"
                      arrow
                    >
                      <HelpIcon size={14} style={{ cursor: 'help' }} />
                    </Tooltip>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                    }}
                  >
                    Use the form above to enter the customer&apos;s current contract details
                    (members, features, etc.), then click the button below to set it as the baseline
                    for comparison.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    color="warning"
                    onClick={captureBaseline}
                    sx={{ textTransform: 'none' }}
                  >
                    Set Current Form Values as Baseline
                  </Button>
                </>
              ) : isEditingBaseline ? (
                // Editing mode: User is modifying the baseline
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: theme.palette.warning.main,
                    }}
                  >
                    Editing Baseline
                    <Tooltip
                      title="Modify the form above, then click 'Save Baseline' when done"
                      arrow
                    >
                      <HelpIcon size={14} style={{ cursor: 'help' }} />
                    </Tooltip>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 2,
                    }}
                  >
                    Modify the form values above to match the customer&apos;s current contract, then
                    save.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      color="warning"
                      onClick={captureBaseline}
                      sx={{ textTransform: 'none' }}
                    >
                      Save Baseline
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={cancelBaselineEdit}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </>
              ) : (
                // Step 2: Baseline is set - show summary and allow editing proposal
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      Current Contract Baseline
                      <Tooltip
                        title="This is the customer's current contract. Modify the form above to build the renewal proposal."
                        arrow
                      >
                        <HelpIcon size={14} style={{ cursor: 'help' }} />
                      </Tooltip>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit baseline values" arrow>
                        <IconButton
                          size="small"
                          onClick={editBaseline}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <PencilIcon size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear baseline" arrow>
                        <IconButton
                          size="small"
                          onClick={() => setCurrentContract(null)}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <XIcon size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                    <Typography variant="body2">
                      <strong>{formatCurrency(currentContract.totalPrice)}</strong>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {currentContract.numberOfMembers} members
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {currentContract.subscriptionDuration} months
                    </Typography>
                    {currentContract.securityPackageSso && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        SSO
                      </Typography>
                    )}
                    {currentContract.tenantType === TenantType.SINGLE && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Single Tenant
                      </Typography>
                    )}
                    {currentContract.tenantType === TenantType.ENTERPRISE && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Enterprise Tenant
                      </Typography>
                    )}
                    {currentContract.govCloud && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Gov Cloud
                      </Typography>
                    )}
                    {currentContract.selectedIntegrations.length > 0 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {currentContract.selectedIntegrations.length} integration(s)
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Now modify the form above to build your renewal proposal. The comparison below
                    will update automatically.
                  </Typography>
                </>
              )}
            </Box>
          </Collapse>
        </Box>
      </PricingCalculatorSection>

      {/* Member Pricing Section */}
      <PricingCalculatorSection
        title="Member Pricing"
        subtitle={formatCurrency(memberPricingSubtotal)}
        icon={<UsersIcon size={18} />}
        hasSelections={
          values.numberOfMembers > 1 ||
          values.numberOfInternalContractors > 0 ||
          values.numberOfGuests > 0
        }
        defaultExpanded={true}
        expanded={sectionStates.memberPricing}
        onExpandedChange={() => toggleSection('memberPricing')}
      >
        {/* Plan Selection */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Select Plan (Optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'nowrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FeatureToggle
              label="Base Plan"
              checked={values.planType === PlanType.BASE}
              onChange={(checked) => {
                handlePlanTypeChange(PlanType.BASE)({
                  target: { checked },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              description={`$9.99/member, $${BASE_PLAN_FLAT_FEE.toLocaleString('en-US')} platform fee (≤${BASE_PLAN_FLAT_FEE_MAX_USERS} users)`}
              name="basePlan"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <FeatureToggle
              label="Mosaic Enterprise Plan"
              checked={values.planType === PlanType.MOSAIC_ENTERPRISE}
              onChange={(checked) => {
                handlePlanTypeChange(PlanType.MOSAIC_ENTERPRISE)({
                  target: { checked },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              description={`Published Enterprise rate (≈2× Standard fee), 2× hours (${MOSAIC_ENTERPRISE_MIN_USERS}+ users)`}
              name="mosaicEnterprisePlan"
              disabled={values.numberOfMembers < MOSAIC_ENTERPRISE_MIN_USERS}
              note={
                values.numberOfMembers < MOSAIC_ENTERPRISE_MIN_USERS
                  ? `Not available for under ${MOSAIC_ENTERPRISE_MIN_USERS} users`
                  : undefined
              }
            />
          </Box>
        </Box>

        {/* Member License Cost */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Member License Cost
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Tooltip title="Automatically updates price and platform fee" arrow placement="top">
            <TextField
              label="Number of Members"
              name="numberOfMembers"
              type="number"
              value={values.numberOfMembers}
              onChange={handleNumberOfMembersChange}
              error={!!validation.errors.numberOfMembers}
              helperText={validation.errors.numberOfMembers}
              sx={{ width: 200 }}
            />
          </Tooltip>
          <FormControl sx={{ width: 200 }}>
            <InputLabel id="price-per-member-label">Price Per Member</InputLabel>
            <Select
              labelId="price-per-member-label"
              label="Price Per Member"
              value={values.pricePerMember}
              onChange={(e) => {
                setValues((prev) => ({
                  ...prev,
                  pricePerMember: Number(e.target.value),
                }));
              }}
              disabled={values.planType === PlanType.BASE}
            >
              {(() => {
                const basePrices = [9.99, 12.99, 14.99, 19.99, 24.99];
                const isEnterprise = values.planType === PlanType.MOSAIC_ENTERPRISE;
                const multiplier = 1.5;
                const prices = isEnterprise
                  ? basePrices.map((price) => Math.round(price * multiplier * 100) / 100)
                  : basePrices;
                return prices.map((price) => (
                  <MenuItem key={price} value={price}>
                    ${price.toFixed(2)}
                  </MenuItem>
                ));
              })()}
            </Select>
          </FormControl>
          <TextField
            label="After Discount"
            type="number"
            value={afterDiscountInput}
            onFocus={() => {
              isEditingAfterDiscountRef.current = true;
            }}
            onChange={(e) => setUserAfterDiscountInput(e.target.value)}
            onBlur={() => {
              isEditingAfterDiscountRef.current = false;
              const afterDiscountPrice = parseFloat(afterDiscountInput);
              if (!isNaN(afterDiscountPrice) && afterDiscountPrice >= 0) {
                const calculatedDiscount =
                  values.pricePerMember > 0
                    ? ((values.pricePerMember - afterDiscountPrice) / values.pricePerMember) * 100
                    : 0;
                setValues((prev) => ({
                  ...prev,
                  discountOnMemberPrice: Math.max(0, Math.min(100, calculatedDiscount)),
                }));
                setUserAfterDiscountInput(null);
              }
            }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
            }}
            sx={{ width: 150 }}
          />
          <TextField
            label="Discount"
            value={values.discountOnMemberPrice.toFixed(2)}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
              htmlInput: { readOnly: true },
            }}
            disabled
            sx={{ width: 120 }}
          />
        </Box>

        {/* Total for members - inline summary */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            pl: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Subtotal ({values.numberOfMembers} members × {values.subscriptionDuration} mo):
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
            {formatCurrency(
              values.numberOfMembers *
                values.pricePerMember *
                (1 - values.discountOnMemberPrice / 100) *
                values.subscriptionDuration,
            )}
          </Typography>
        </Box>

        {/* Tier Indicators - Visual feedback for volume discounts */}
        {values.numberOfMembers >= 25 && values.planType !== PlanType.BASE && (
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Your Pricing Tiers
              <Tooltip title="As your user count increases, you may qualify for volume discounts. The tier indicators show your current pricing tier and what's needed for the next discount level.">
                <HelpIcon size={14} style={{ cursor: 'help' }} />
              </Tooltip>
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 280, maxWidth: 'calc(50% - 8px)' }}>
                <TierIndicator
                  userCount={values.numberOfMembers}
                  tierType="seat"
                  planType={values.planType}
                  title="Member Price Tier"
                  showComparison={true}
                  showProgress={true}
                  enableAnimations={true}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 280, maxWidth: 'calc(50% - 8px)' }}>
                <TierIndicator
                  userCount={values.numberOfMembers}
                  tierType="platform"
                  planType={values.planType}
                  title="Platform Fee Tier"
                  showComparison={true}
                  showProgress={true}
                  enableAnimations={true}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Internal Contractors */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Internal Contractors
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <TextField
            label="Number of Internal Contractors"
            name="numberOfInternalContractors"
            type="number"
            value={values.numberOfInternalContractors || ''}
            onChange={handleInputChange('numberOfInternalContractors')}
            helperText={`$${PRICING_CONSTANTS.INTERNAL_CONTRACTOR_COST}/month each`}
            placeholder="0"
            sx={{ width: 250 }}
          />
        </Box>

        {/* Guests */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Guests
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <TextField
            label="Number of Guests"
            name="numberOfGuests"
            type="number"
            value={values.numberOfGuests || ''}
            onChange={handleInputChange('numberOfGuests')}
            helperText={`$${PRICING_CONSTANTS.GUEST_COST}/month each`}
            placeholder="0"
            sx={{ width: 250 }}
          />
        </Box>

        {/* External Contractors */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          External Contractors
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Number of External Contractors"
            name="numberOfExternalContractors"
            type="number"
            value={values.numberOfExternalContractors || ''}
            onChange={handleInputChange('numberOfExternalContractors')}
            helperText={`$${PRICING_CONSTANTS.EXTERNAL_CONTRACTOR_COST}/month each`}
            placeholder="0"
            sx={{ width: 250 }}
          />
        </Box>
      </PricingCalculatorSection>

      {/* Platform & Onboarding Section */}
      <PlatformOnboardingSection
        values={values}
        summary={summary}
        handleInputChange={handleInputChange}
        platformSubtotal={platformSubtotal}
        expanded={sectionStates.platformOnboarding}
        onExpandedChange={() => toggleSection('platformOnboarding')}
      />

      {/* Implementation Options Section */}
      <ImplementationOptionsSection
        values={values}
        setValues={setValues}
        summary={summary}
        handleInputChange={handleInputChange}
        serverSubtotal={serverSubtotal}
        expanded={sectionStates.serverUpgrades}
        onExpandedChange={() => toggleSection('serverUpgrades')}
        handleDirectDatabaseChange={handleDirectDatabaseChange}
      />

      {/* Add-ons Section */}
      <AddonsSection
        values={values}
        setValues={setValues}
        summary={summary}
        addonsSubtotal={addonsSubtotal}
        expanded={sectionStates.addons}
        onExpandedChange={() => toggleSection('addons')}
        handleInputChange={handleInputChange}
      />

      {/* Integrations Section */}
      <PricingCalculatorSection
        title="Integrations"
        subtitle={formatCurrency(integrationsSubtotal)}
        icon={<LinkIcon size={18} />}
        hasSelections={values.selectedIntegrations.length > 0}
        defaultExpanded={false}
        expanded={sectionStates.integrations}
        onExpandedChange={() => toggleSection('integrations')}
      >
        <PricingCalculatorIntegrationsMultiSelector {...integrationsSelectorProps} />

        {values.selectedIntegrations.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Integration Details
            </Typography>
            {values.selectedIntegrations.map((integrationKey) => {
              // GLX-1746: only label as "Newly Added" when a baseline contract
              // is known (originalIntegrations !== undefined). An undefined
              // baseline (no contract loaded) means we can't make the claim, so
              // suppress the badge.
              const isNewlyAdded =
                originalIntegrations !== undefined &&
                !originalIntegrations.includes(integrationKey);
              return (
                <PricingCalculatorIntegrationDetail
                  key={integrationKey}
                  integrationKey={integrationKey}
                  numberOfMembers={values.numberOfMembers}
                  isRenewal={values.isRenewal}
                  isMigration={integrationMigrations[integrationKey] || false}
                  isGovCloud={values.govCloud}
                  isSingleTenant={isDedicatedTenant(values.tenantType)}
                  is4xDataTransfer={values.enable4xDataTransfer}
                  discountOnSetup={integrationSetupDiscounts[integrationKey] || 0}
                  discountOnRecurring={integrationRecurringDiscounts[integrationKey] || 0}
                  isNewlyAdded={isNewlyAdded}
                  pipelineOpportunities={integrationPipelineOpportunities[integrationKey] || 1}
                  onMigrationChange={handleIntegrationMigrationChange}
                  onDiscountChange={handleIntegrationDiscountChange}
                  onPipelineOpportunitiesChange={handleIntegrationPipelineOpportunitiesChange}
                  onRemove={(key) =>
                    integrationsSelectorProps.onSelectIntegration(
                      values.selectedIntegrations.filter((k) => k !== key),
                    )
                  }
                />
              );
            })}
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <FeatureToggle
              label="4x Data Transfer"
              checked={values.enable4xDataTransfer}
              onChange={(checked) =>
                setValues((prev) => ({ ...prev, enable4xDataTransfer: checked }))
              }
              description="Increase from 2x to 4x daily syncs (+30% on recurring fees)"
              name="enable4xDataTransfer"
            />
          </Box>
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  4x Data Transfer
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • Standard: 2x daily syncs
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  • With 4x: 4 daily syncs (+30% recurring fees)
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  Best for real-time data requirements
                </Typography>
              </Box>
            }
            arrow
            placement="right"
          >
            <Box sx={{ display: 'flex', cursor: 'help', color: 'text.secondary', mt: 1 }}>
              <HelpIcon size={16} />
            </Box>
          </Tooltip>
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            label="Bak Integration Fetch Times"
            name="bakIntegrationFetchTimes"
            type="number"
            value={values.bakIntegrationFetchTimes}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 1;
              const clampedValue = Math.max(1, Math.min(3, value));
              setValues((prev) => ({
                ...prev,
                bakIntegrationFetchTimes: clampedValue,
              }));
            }}
            helperText={`1=free, 2=$${PRICING_CONSTANTS.INTEGRATION_FETCH_COST_2X}/mo, 3=$${PRICING_CONSTANTS.INTEGRATION_FETCH_COST_3X}/mo`}
            sx={{ width: 250 }}
            slotProps={{
              htmlInput: { min: 1, max: 3, step: 1 },
            }}
          />
        </Box>
      </PricingCalculatorSection>
    </form>
  );

  // Render the Quote Summary (condensed view - independent card)
  const renderQuoteSummary = () => (
    <PricingCalculatorStickyPanel
      values={values}
      summary={summary}
      onReset={activeTab === 0 ? handleReset : undefined}
      getShareableUrl={getShareableUrl}
    />
  );

  // Render the preset selector
  // On Pricing Quote tab (activeTab === 1): show only export buttons (Copy, Excel, PDF)
  // On Calculator tab (activeTab === 0): show Quick Start presets and Collapse/Expand button in header
  const renderPresetSelector = () => (
    <PricingCalculatorPresets
      onPresetSelect={handlePresetSelect}
      activePreset={activePreset}
      onCopyQuote={activeTab === 1 ? copyQuoteToClipboard : undefined}
      onExportExcel={activeTab === 1 ? exportToExcel : undefined}
      onExportPdf={activeTab === 1 ? exportToPDF : undefined}
      exportOnly={activeTab === 1}
    />
  );

  // Render the formulas content
  const renderFormulasContent = () => (
    <PricingCalculatorFormulas
      values={values}
      summary={summary}
      originalIntegrations={originalIntegrations}
      integrationPipelineOpportunities={integrationPipelineOpportunities}
      integrationSetupDiscounts={integrationSetupDiscounts}
      integrationRecurringDiscounts={integrationRecurringDiscounts}
      integrationMigrations={integrationMigrations}
      allExpanded={allSectionsExpanded}
      onAllExpandedChange={setAllSectionsExpanded}
    />
  );

  // Render the pricing scale table content
  const renderScaleTableContent = () => (
    <PricingScaleTable
      subscriptionDuration={values.subscriptionDuration}
      numberOfMembers={values.numberOfMembers}
      isItCustomer={values.isItCustomer}
      planType={values.planType}
      pricePerMember={values.pricePerMember}
      discountOnMemberPrice={values.discountOnMemberPrice}
      discountOnPlatformFee={values.discountOnPlatformFee}
      // Server upgrades
      tenantType={values.tenantType}
      discountOnTenant={values.discountOnTenant}
      govCloud={values.govCloud}
      discountOnGovCloudFee={values.discountOnGovCloudFee}
      securityPackageSso={values.securityPackageSso}
      discountOnSecurityPackageSso={values.discountOnSecurityPackageSso}
      directDatabaseIntegration={values.directDatabaseIntegration}
      discountOnDirectDatabase={values.discountOnDirectDatabase}
      isolatedDatabase={values.isolatedDatabase}
      discountOnIsolatedDatabase={values.discountOnIsolatedDatabase}
      // Integrations
      selectedIntegrations={values.selectedIntegrations}
      integrationRecurringDiscounts={integrationRecurringDiscounts}
      enable4xDataTransfer={values.enable4xDataTransfer}
      // Add-ons
      aiForecasting={values.aiForecasting}
      discountOnAiForecasting={values.discountOnAiForecasting}
      nonPeopleResources={values.nonPeopleResources}
      discountOnNonPeopleResources={values.discountOnNonPeopleResources}
      profileBuilding={values.profileBuilding}
      discountOnProfileBuilding={values.discountOnProfileBuilding}
      skillsTagging={values.skillsTagging}
      discountOnSkillsTagging={values.discountOnSkillsTagging}
      solutionsSupport={values.solutionsSupport}
      solutionsSupportHours={values.solutionsSupportHours}
      enableWhiteLabel={values.enableWhiteLabel}
    />
  );

  return (
    <PricingCalculatorLayout
      presetSelector={renderPresetSelector()}
      formContent={renderFormContent()}
      quoteSummary={renderQuoteSummary()}
      formulasContent={renderFormulasContent()}
      scaleTableContent={renderScaleTableContent()}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      allExpanded={allSectionsExpanded}
      onToggleExpandAll={handleToggleExpandAll}
    />
  );
};

export default PricingCalculatorFormV2;
